"use client";

import { useEffect, useState, useCallback, memo, useRef } from "react";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/commonFunctions";
import Icon from "../icons/Icons";
import { useDebounce } from "@/utils/useDebounce";
import Image from "next/image";
import Link from "next/link";
import useScrollTrigger from "@/utils/useScrollTrigger";
import { AnimatePresence, motion } from "framer-motion";
import { ROOT_URL } from "@/utils/gloableVariables";

interface SearchModalProps {
  isOpen: boolean;
  onCloseAction: () => void;
}

interface SearchResult {
  title: string;
  link: string;
  type: "funds" | "blogs" | "videos";
}

const MOST_SEARCHED_ITEMS = [
  {
    name: "HDFC Manufacturing Fund - Regular (G)",
    link: "https://www.mysiponline.com/mutual-fund/hdfc-manufacturing-fund/mso6908",
  },
  {
    name: "SBI Energy Opportunities Fund - Regular (G)",
    link: "https://www.mysiponline.com/mutual-fund/sbi-energy-opportunities-fund-regular-plan-growth/mso6803",
  },
  {
    name: "Quant Small Cap Fund-Growth",
    link: "https://www.mysiponline.com/mutual-fund/quant-small-cap-fund-growth/mso6579",
  },
];

const HeaderSearchModal = ({ isOpen, onCloseAction }: SearchModalProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]); 

  useEffect(() => {
    if (isOpen) {
      lockBodyScroll();
      return () => {
        unlockBodyScroll(); // only runs when modal closes or unmounts
      };
    }
  }, [isOpen]);

  const [searchState, setSearchState] = useState({
    query: "",
    results: [] as SearchResult[],
    isLoading: false,
  });

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [activeTab, setActiveTab] = useState<"funds" | "blogs" | "videos">("funds");

  const [recentSearches, setRecentSearches] = useState<Record<string, SearchResult[]>>({});

  useEffect(() => {
    const savedSearches = localStorage.getItem("recentSearchItem");
    if (savedSearches) {
      setRecentSearches(JSON.parse(savedSearches));
    }
  }, []);

  const handleSearch = useCallback(async (value: string, type: "funds" | "blogs" | "videos") => {
    if (!value.trim()) {
      setSearchState((prev) => ({ ...prev, results: [], isLoading: false }));
      return;
    }

    const controller = new AbortController();
    setSearchState((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await fetch(`${ROOT_URL}web-apis.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ search: value, type, action: "search_header" }),
        signal: controller.signal,
      });

      const data = await response.json();
      setSearchState((prev) => ({
        ...prev,
        results: data.data || [],
        isLoading: false,
      }));
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        console.error("Search error:", error);
        setSearchState((prev) => ({ ...prev, isLoading: false }));
      }
    }

    return () => controller.abort();
  }, []);

  const debouncedQuery = useDebounce(searchState.query, 300);
  const debouncedTab = useDebounce(activeTab, 300);

  useEffect(() => {
    if (debouncedQuery.trim()) {
      handleSearch(debouncedQuery, debouncedTab);
    } else {
      setSearchState((prev) => ({ ...prev, results: [] }));
    }
  }, [debouncedQuery, debouncedTab, handleSearch]);

  const handleSearchInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchState((prev) => ({ ...prev, query: value, isLoading: !!value.trim(), results: [] }));
  }, []);

  const handleTabChange = useCallback(
    (newTab: "funds" | "blogs" | "videos") => {
      setActiveTab(newTab);

      if (searchState.query.trim()) {
        setSearchState((prev) => ({
          ...prev,
          isLoading: true,
          results: [],
        }));
      }
    },
    [searchState.query],
  );

  const clearSearch = useCallback(() => {
    setSearchState((prev) => ({ ...prev, query: "", results: [] }));
  }, []);

  const addToRecentSearches = useCallback((result: SearchResult) => {
    setRecentSearches((prev) => {
      const updated = { ...prev };
      if (!updated[result.type]) {
        updated[result.type] = [];
      }
      if (!updated[result.type].some((item) => item.title === result.title)) {
        updated[result.type] = [...updated[result.type], result];
      }
      localStorage.setItem("recentSearchItem", JSON.stringify(updated));
      return updated;
    });
  }, []);

  const clearAllRecentSearches = () => {
    localStorage.removeItem("recentSearchItem");
    setRecentSearches({});
  };

  const removeRecentSearch = (type: string, index: number) => {
    const updatedSearches = { ...recentSearches };
    if (updatedSearches[type]) {
      updatedSearches[type].splice(index, 1);
      if (updatedSearches[type].length === 0) {
        delete updatedSearches[type];
      }
      setRecentSearches(updatedSearches);
      localStorage.setItem("recentSearchItem", JSON.stringify(updatedSearches));
    }
  };

  const isScrolled = useScrollTrigger();

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/40 backdrop-blur-lg z-4000"
          onClick={onCloseAction}
          style={{ touchAction: "manipulation" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: isOpen ? 1 : 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          {/* Search Modal */}
          <motion.div
            // className={`fixed ${isScrolled ? "top-0 sm:top-27.5" : "top-13.5 sm:top-27.5"} left-0 right-0 z-4500 mx-auto sm:max-w-167.75 transition-all duration-400 ease-in-out ${isOpen ? "scale-100 opacity-100 translate-y-[0px] pointer-events-auto visible" : "scale-85 opacity-0 pointer-events-none translate-y-[20px] invisible"}`}

            initial={{ opacity: 0, scale: 0.85, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 20 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            onClick={(e) => e.stopPropagation()}
            className={`absolute ${isScrolled ? "top-0 sm:top-27.5" : "top-13.5 sm:top-27.5"} inset-x-0 z-1 mx-auto sm:max-w-167.75`}
          >
            <div className={`bg-white sm:bg-transparent ${isScrolled ? "pt-20" : "pt-0"}`}>
              {/* Search Input */}
              <div className="relative bg-white p-3.25 flex items-center gap-2 xs:p-2.75 xs:border-b xs:border-   [#CECECE] sm:mb-2.5 mb-0 sm:rounded-xl rounded-none">
                <span className={`w-5 h-5`} onClick={isMobile ? onCloseAction : undefined}>
                  {isMobile ? <Icon.closeSearchIcon className="-rotate-90 w-5 h-5" /> : <Icon.searchIcon />}
                </span>
                <input
                  ref={inputRef}
                  type="text"
                  value={searchState.query}
                  onChange={handleSearchInput}
                  placeholder="Search for Easy Mutual funds..."
                  className="w-[90%] bg-transparent border-none outline-hidden font-normal text-base text-[#7B7B7B]
                xs:text-sm xs:leading-[16.75px]"
                />
                {searchState.query && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-5 w-3 h-3 cursor-pointer text-[#7B7B7B] transition duration-300 hover:text-primaryBlue"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none">
                      <line
                        x1="1"
                        y1="1"
                        x2="15"
                        y2="15"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                      />
                      <line
                        x1="1"
                        y1="15"
                        x2="15"
                        y2="1"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                )}
              </div>

              <div className="bg-white sm:rounded-xl rounded-none overflow-hidden sm:h-[unset] h-svh ">
                {/* Tabs */}
                <div className="flex items-center gap-2 p-5 pb-0 mb-4.25 xs:p-3.5 xs:pb-0 xs:mb-3">
                  {["funds", "blogs", "videos"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => handleTabChange(tab as "funds" | "blogs" | "videos")}
                      className={`flex items-center gap-1.5 px-2.75 py-[6.5px] rounded-5xl text-sm font-normal cursor-pointer transition-all duration-300
                    xs:gap-1.5 xs:px-2.5 xs:py-1.25 xs:rounded-3xl xs:text-sm xs:leading-[14.41px]
                    ${activeTab === tab ? "bg-(--secondary) text-white border border-(--secondary)" : "bg-white text-[#09090B] border border-[#6C6C6C] hover:border-(--secondary) hover:bg-[#E1E8FF]"}`}
                    >
                      {tab === "funds" ? (
                        <Icon.mutualFundIcon className={`w-5 h-5 xs:w-4 xs:h-4 ${activeTab === tab ? "invert" : ""}`} />
                      ) : tab === "blogs" ? (
                        <Icon.blogIcon className={`w-5 h-5 xs:w-4 xs:h-4 ${activeTab === tab ? "invert" : ""}`} />
                      ) : (
                        <Icon.videoIcon className={`w-5 h-5 xs:w-4 xs:h-4 ${activeTab === tab ? "invert" : ""}`} />
                      )}
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>

                {/* Results */}
                <div className="relative">
                  {searchState.isLoading ? (
                    <div className="p-5">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="flex gap-2.5 mb-2.5">
                          <div className="w-6 h-6 rounded-full animate-pulse bg-gray-200" />
                          <div className="flex-1 h-6 rounded-full animate-pulse bg-gray-200" />
                        </div>
                      ))}
                    </div>
                  ) : searchState.query ? (
                    <>
                      {searchState.results.length > 0 ? (
                        <ul className="px-1 sm:max-h-60 max-h-[calc(100vh-210px)] overflow-y-auto scrollbar-sm">
                          {searchState.results.map((result, index) => (
                            <li
                              key={index}
                              onClick={() => addToRecentSearches(result)}
                              className="rounded-lg opacity-60 hover:bg-[#E1E8FF] hover:opacity-100 transition-all duration-300 ease-in-out cursor-pointer"
                            >
                              <Link
                                href={result.link}
                                target="_blank"
                                rel="nofollow noopener noreferrer"
                                className="flex items-center gap-3.5 p-2.5 xs:gap-1.5"
                              >
                                <span
                                  className={`w-8 h-8 shrink-0 rounded-full border border-[#7E7E7E]/20 bg-white flex items-center justify-center`}
                                >
                                  {activeTab === "funds" ? (
                                    <Icon.mutualFundIcon className={`w-5 h-5 xs:w-4 xs:h-4`} />
                                  ) : activeTab === "blogs" ? (
                                    <Icon.blogIcon className={`w-5 h-5 xs:w-4 xs:h-4`} />
                                  ) : (
                                    <Icon.ytVideoIcon className={`w-5 h-5 xs:w-4 xs:h-4`} />
                                  )}
                                </span>

                                <p className="flex-1 text-[#09090B] truncate text-sm leading-4 sm:text-lg sm:leading-6.5">
                                  {result.title}
                                </p>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="pb-5">
                          <div className="h-65 relative xs:h-52.5 p-5 xs:p-3 xs:gap-3 z-2 flex flex-col items-center justify-end gap-4">
                            <p className="text-base font-medium text-[#09090B] relative z-1 xs:text-xs xs:leading-[14.75px]">
                              No results found
                            </p>
                            <p className="text-base text-[#6C6C6C] xs:text-xs relative z-1 xs:leading-[14.75px]">
                              &ldquo;{searchState.query}&rdquo; did not match any {activeTab}. Please try again.
                            </p>
                            <button
                              onClick={clearSearch}
                              className="px-6 py-2.5 border border-[#C9C9C9] relative z-1 rounded-lg hover:bg-[#C9C9C9] transition-all duration-500 ease-in-out xs:px-4.5 xs:py-2 text-[#222222] font-medium text-base cursor-pointer"
                            >
                              Clear Search
                            </button>
                          </div>
                          <Image
                            src={`${ROOT_URL}images/include-assets/bg_no_data.svg`}
                            alt="search-modal-image"
                            width={671}
                            height={260}
                            className="absolute -bottom-5 scale-[1.2] left-0 z-0 w-full h-full object-contain pointer-events-none"
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="p-5 border-t border-[#EAEAEA] xs:p-3">
                      {Object.keys(recentSearches).length > 0 ? (
                        <>
                          <div className="flex justify-between items-center mb-3">
                            <p className="text-base font-medium text-[#444444] xs:text-sm xs:leading-[14.75px]">
                              Recent Searches
                            </p>
                            <button
                              onClick={clearAllRecentSearches}
                              className="text-sm font-medium text-[#6C6C6C] cursor-pointer underline hover:text-(--text) transition-all duration-300 ease-in-out"
                            >
                              Clear All
                            </button>
                          </div>

                          <ul className="flex flex-wrap gap-3 max-h-72 overflow-y-auto pr-1 scrollbar-sm">
                            {Object.entries(recentSearches).map(([type, searches]) =>
                              searches.map((search, index) => (
                                <li
                                  key={`${type}-${index}`}
                                  className="flex items-center gap-2.5 px-1.25 py-1 rounded-5xl border border-[#09090B] hover:bg-[#E6EBFD] transition-all duration-300 ease-in-out relative
                              xs:px-2.5 xs:py-1.25 xs:gap-1.5"
                                >
                                  <span className="w-6.5 h-6.5 shrink-0 rounded-full border border-[#7E7E7E]/20 bg-white flex items-center justify-center">
                                    {search.type === "funds" ? (
                                      <Icon.mutualFundIcon className={`w-4 h-4`} />
                                    ) : search.type === "blogs" ? (
                                      <Icon.blogIcon className={`w-4 h-4`} />
                                    ) : (
                                      <Icon.ytVideoIcon className={`w-4 h-4`} />
                                    )}
                                  </span>
                                  <Link
                                    href={search.link}
                                    target="_blank"
                                    rel="nofollow noopener noreferrer"
                                    className="text-sm font-normal text-[#09090B] line-clamp-2 xs:text-base xs:leading-1.3"
                                  >
                                    {search.title}
                                  </Link>

                                  <span
                                    onClick={() => removeRecentSearch(type, index)}
                                    className="inline-flex items-center justify-center shrink-0 w-6 h-6 rounded-full hover:bg-[#b1c2ff] transition-all duration-300 ease-in-out cursor-pointer"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 16 16"
                                      fill="none"
                                      className="w-3 h-3 shrink-0"
                                    >
                                      <line
                                        x1="1"
                                        y1="1"
                                        x2="15"
                                        y2="15"
                                        stroke="#7B7B7B"
                                        strokeWidth="1.2"
                                        strokeLinecap="round"
                                      />
                                      <line
                                        x1="1"
                                        y1="15"
                                        x2="15"
                                        y2="1"
                                        stroke="#7B7B7B"
                                        strokeWidth="1.2"
                                        strokeLinecap="round"
                                      />
                                    </svg>
                                  </span>
                                </li>
                              )),
                            )}
                          </ul>
                        </>
                      ) : (
                        <>
                          <div className="mb-3">
                            <p className="text-base font-medium text-[#444444] xs:text-sm xs:leading-[14.75px]">
                              Most Searches
                            </p>
                          </div>
                          <ul className="flex flex-wrap gap-3">
                            {MOST_SEARCHED_ITEMS.map((item, index) => (
                              <li
                                key={index}
                                className="flex items-center gap-2.5 px-2.5 py-1 rounded-5xl border border-[#09090B] hover:bg-[#E6EBFD] transition-colors duration-200
                            xs:px-2.5 xs:py-1.25 xs:gap-1.5"
                              >
                                <span className="w-6.5 h-6.5 shrink-0 rounded-full border border-[#7E7E7E]/20 bg-white flex items-center justify-center ">
                                  <Icon.mutualFundIcon className={`w-5 h-5 xs:w-4 xs:h-4`} />
                                </span>
                                <Link
                                  href={item.link}
                                  target="_blank"
                                  rel="nofollow noopener noreferrer"
                                  className="text-[#09090B] line-clamp-2 text-2xs xs:text-sm xs:leading-1.3"
                                >
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

HeaderSearchModal.displayName = "HeaderSearchModal";

export default memo(HeaderSearchModal);
